import { useGetMainList, useListContext,
  useVersion } from "ra-core"
import { FC, useMemo } from "react"
import { Box, Button, Card, CardHeader, Chip, Divider,
  Typography} from '@mui/material'
import { Link as RouteLink } from "react-router-dom"
import { Location } from "history"
import Scrollbar from '~/components/Scrollbar'
import { Datagrid } from '~/components/list/datagrid'
import TextField from '~/components/field/TextField'
import { Pagination as DefaultPagination } from '~/components/list/pagination'
import { EmailField, FunctionField } from "~/components/field"
import { UserResponseDto, UserStatus } from "~/gen/api/user"
import { RecPatch } from "~/dataProvider/tools"
import ArrowForwardIcon from "@mui/icons-material/ArrowForward"
import DeleteButton from "~/components/button/DeleteButton"
import EditUser from "./EditUser"
import DateField2 from "~/components/field/DateField2"
import { useDefaultSavedHandlers } from "~/controller/defaultSavedHandlers"
import { useHasPermit } from "~/authProvider/useHasPermit"
import { ORGANIZATION_USERS } from "~/constants"
import { pages, sorts } from "~/utils/paramsWithGetMainList"
import { DataAccessRoleResponseDto } from "~/gen/api/governance"
import UserTableSearchRegion from "./UsersTableSearchRegion"
import UsersTableSearchProvider from "./UsersTableSearchProvider"
import { useUserManageAmbientDataContext } from "./UserManageAmbientDataContextProvider"


export interface UsersTableProps {
  currentUserId: number | undefined;
}

const UsersTable: FC<UsersTableProps> = ({ currentUserId }) => {
  const hasWritePermit = useHasPermit([ORGANIZATION_USERS])
  const listContext = useListContext()
  const version = useVersion()
  
  const {roles} = useUserManageAmbientDataContext()

  const { data: dataAccessRoleData } = useGetMainList('settings/governance/data-access-role', pages(), sorts(), {});
  const dataAccessRoles = useMemo(() => Object.values<DataAccessRoleResponseDto>(dataAccessRoleData ?? {} as any), [dataAccessRoleData])

  const handlers = useDefaultSavedHandlers("Delete User")
  handlers.onSuccess.merge(listContext.refetch)

  return (
      <Card>
        <CardHeader
          title="Users"
          action={
            <Button variant="text" size="small" component={RouteLink}
              to={(location: Location) => ({...location, search: '?manage-role=true' })}
              endIcon={<ArrowForwardIcon />}
            >
              Manage Roles
            </Button>
          }
        />
        <Divider />
        <UsersTableSearchProvider>
          <UserTableSearchRegion />
        </UsersTableSearchProvider>
        <Divider />
        <Scrollbar>
          <Box 
            sx={{ 
              minWidth: 1150,
              '& th:last-of-type': { textAlign: 'center' },
            }}
          >

            <Datagrid hasBulkActions={false} {...listContext }>
              <TextField source="firstName" label="First Name" sortable={false} />
              <TextField source="lastName" label="Last Name" sortable={false} />
              <EmailField source="username" label="Email" sortable={false} />
              <FunctionField 
                source="status" 
                label="Status" 
                sortable={false}
                render={(record?: UserResponseDto & RecPatch) => 
                  <Chip 
                    label={typeof record?.status !== 'undefined' 
                      ? (record.status === UserStatus.Active ? 'Active' : 'Inactive')
                      : 'unknown' 
                    }
                    color={(record?.status === UserStatus.Active ? 'success' : 'warning') as any}
                  />
                }
              />
              <DateField2 source="lastLoginTime" label="Last Login" showTime={true} sortable={false} />
              <FunctionField 
                source="rolesText" 
                label="Role" 
                sortable={false}
                render={(record?: UserResponseDto & RecPatch) =>
                  <Typography
                    component="span"
                    variant="body2"
                  >
                    {!record ? '' :
                      record.roles ? record.roles.map(r => r.name || '').join(', ') : record.rolesText}
                  </Typography>
                }
              />
              {hasWritePermit &&
                <FunctionField 
                  source="rolesText" 
                  label="Action" 
                  textAlign="center"
                  sortable={false}
                  render={(record?: UserResponseDto & RecPatch) =>
                    !record ? null :
                    <>
                      <EditUser user={record} roles={roles} dataAccessRoles={dataAccessRoles} currentUserId={currentUserId} />
                      {currentUserId !== record?.id &&
                        <DeleteButton resource="settings/organization/user-manage" record={record} basePath="" {...handlers} />}
                    </>
                  }
                />}
            </Datagrid>

          </Box>
        </Scrollbar>
        {//@ts-ignore
          <DefaultPagination {...listContext} />}
      </Card>
  )
}

export default UsersTable;
